import {
	autoUpdate,
	FloatingFocusManager,
	FloatingPortal,
	flip,
	offset,
	shift,
	size,
	useClick,
	useDismiss,
	useFloating,
	useInteractions,
	useListNavigation,
	useRole,
} from "@floating-ui/react";
import { useMemo, useRef, useState } from "react";
import { FaChevronDown, FaTimes } from "react-icons/fa";

const SelectMultipleInputs = ({
	id,
	placeholder,
	value = [],
	onChange,
	options,
	label,
	displayField = "name",
	renderOption,
	disabled,
}) => {
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const [searchTerm, setSearchTerm] = useState("");
	const [activeIndex, setActiveIndex] = useState(null);

	const listRef = useRef([]);

	const { refs, floatingStyles, context } = useFloating({
		open: isDropdownOpen,
		onOpenChange: (open) => {
			setIsDropdownOpen(open);
			if (!open) setSearchTerm("");
		},
		placement: "bottom-start",
		middleware: [
			offset(4),
			flip({ padding: 8 }),
			shift({ padding: 8 }),
			size({
				apply({ rects, elements, availableHeight }) {
					Object.assign(elements.floating.style, {
						width: `${rects.reference.width}px`,
						maxHeight: `${Math.min(240, availableHeight)}px`,
					});
				},
				padding: 8,
			}),
		],
		whileElementsMounted: autoUpdate,
	});

	const click = useClick(context, { enabled: !disabled });
	const dismiss = useDismiss(context);
	const role = useRole(context, { role: "listbox" });
	const listNav = useListNavigation(context, {
		listRef,
		activeIndex,
		onNavigate: setActiveIndex,
		virtual: true,
		loop: true,
	});

	const { getReferenceProps, getFloatingProps, getItemProps } = useInteractions([
		click,
		dismiss,
		role,
		listNav,
	]);

	const selectedValues = Array.isArray(value) ? value : [];

	const filteredOptions = useMemo(() => {
		if (!options?.length) return [];
		return options.filter((option) =>
			(option?.[displayField] || "").toLowerCase().includes(searchTerm.toLowerCase()),
		);
	}, [options, displayField, searchTerm]);

	const handleSelectChange = (selectedValue) => {
		let newValue = Array.isArray(value) ? [...value] : [];
		if (newValue.includes(selectedValue)) {
			newValue = newValue.filter((val) => val !== selectedValue);
		} else {
			newValue.push(selectedValue);
		}
		onChange(id, newValue);
	};

	const handleClear = (e) => {
		e.stopPropagation();
		onChange(id, []);
	};

	const handleKeyDown = (e) => {
		if (e.key === "Enter" && activeIndex != null && filteredOptions[activeIndex]) {
			e.preventDefault();
			handleSelectChange(filteredOptions[activeIndex].id);
		}
	};

	const selectedLabel =
		value?.length > 0
			? value.map((val) => options.find((option) => option.id === val)?.[displayField]).join(", ")
			: "";

	return (
		<div className={`relative flex flex-col ${disabled ? "disabled-select" : ""}`}>
			<label
				htmlFor={id}
				className="mb-2 flex items-center text-left font-normal text-gray-400 text-xs"
			>
				{label}
			</label>
			<div
				ref={refs.setReference}
				className="custom-scrollbar flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-600 bg-blue-gray bg-opacity-20 p-2.5 text-gray-400 text-xs placeholder-gray-400 focus:border-gray-500 focus:ring-gray-500"
				{...getReferenceProps()}
			>
				<div className="flex w-full items-center">
					<span className="flex-1 truncate text-left text-xs" title={selectedLabel}>
						{value?.length > 0 ? selectedLabel : placeholder}
					</span>
					{value.length > 0 && (
						<FaTimes className="ml-2 cursor-pointer text-gray-400" onClick={handleClear} />
					)}
					<FaChevronDown
						className={`ml-2 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}
					/>
				</div>
			</div>

			{isDropdownOpen && (
				<FloatingPortal>
					<FloatingFocusManager context={context} initialFocus={-1}>
						<div
							ref={refs.setFloating}
							style={floatingStyles}
							className="custom-scrollbar z-50 max-h-60 overflow-y-auto overflow-x-hidden rounded-lg border border-gray-600 bg-navbar-bg"
							{...getFloatingProps({ onKeyDown: handleKeyDown })}
						>
							<input
								type="text"
								value={searchTerm}
								onChange={(e) => setSearchTerm(e.target.value)}
								placeholder={`Search ${label ? label : ""}`}
								className="w-full truncate border-gray-600 border-b bg-navbar-bg p-2.5 text-gray-400 text-xs placeholder-gray-400 focus:border-blue-gray focus:ring-blue-gray"
							/>
							{filteredOptions?.length > 0 ? (
								filteredOptions.map((option, index) => (
									<div
										key={option.id}
										ref={(node) => {
											listRef.current[index] = node;
										}}
										className={`cursor-pointer truncate p-2 text-left text-sm text-white hover:bg-blue-gray hover:bg-opacity-40 ${
											selectedValues?.includes(option.id) ? "bg-blue-gray bg-opacity-80" : ""
										} ${
											activeIndex === index && !selectedValues?.includes(option.id)
												? "bg-blue-gray bg-opacity-40"
												: ""
										}`}
										{...getItemProps({
											onClick: () => handleSelectChange(option.id),
										})}
									>
										{renderOption ? renderOption(option) : option[displayField]}
									</div>
								))
							) : (
								<div className="p-2 text-gray-400">No options found</div>
							)}
						</div>
					</FloatingFocusManager>
				</FloatingPortal>
			)}
		</div>
	);
};

export default SelectMultipleInputs;
